import React from 'react'
import ReactDOM from 'react-dom'
import { createRemoteReceiver } from '@remote-ui/core'
import { ExternalComponentBridge } from '../bridge/createExternalComponentBridge'
import getRemoteChannel from './getRemoteChannel'
import createController from './createController'
import prepareExternalComponent from '../components/ExternalComponent'
import type { RemotePortalCreator } from '../types'

const PORTALS: Record<string, React.ReactPortal> = {}

export interface RemotePortalProps {
	id: string
}

export const RemotePortal: React.FC<RemotePortalProps> = ({ id }) => {
	return PORTALS[id] || null
}

export const getRemotePortalCreator = (externalComponentBridge: ExternalComponentBridge): RemotePortalCreator => async (
	container: Element,
	id = Math.random().toString()
) => {
	if (!isValidContainer(container)) {
		// Same error as ReactDOM to ensure users can Google it
		// https://github.com/facebook/react/blob/9cfba0f6ecc15a342ef184552742c5db6c7f2d3e/packages/react-dom/src/client/ReactDOMRoot.js#L186
		throw new Error('Target container is not a DOM element.')
	}

	const receiver = createRemoteReceiver()
	const { channel } = getRemoteChannel(receiver)

	const controller = createController({ allowHtml: true })
	const ExternalComponent = prepareExternalComponent({ receiver, controller, remoteRender: () => {} })

	PORTALS[id] = ReactDOM.createPortal(React.createElement(ExternalComponent, null), container)

	externalComponentBridge.onHostPortalAppended(id, channel) // This is calling a callback defined in "Portal" component (in sandbox)

	return {
		id,
	}
}

// Taken from ReactDOM to ensure compatibility
// https://github.com/facebook/react/blob/9cfba0f6ecc15a342ef184552742c5db6c7f2d3e/packages/react-dom/src/client/ReactDOMRoot.js#L360-L370
function isValidContainer(node: Element): boolean {
	return !!(
		node &&
		(node.nodeType === Node.ELEMENT_NODE ||
			node.nodeType === Node.DOCUMENT_NODE ||
			node.nodeType === Node.DOCUMENT_FRAGMENT_NODE ||
			(node.nodeType === Node.COMMENT_NODE && node.nodeValue === ' react-mount-point-unstable '))
	)
}
